import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, RefreshCw, Server } from 'lucide-react';
import { API, toast } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatOpsActionLabel } from '@/utils/displayLabels';
import { getAuthToken } from '@/utils/authSession';

const numberOrZero = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

const OperationsOverview = ({ embedded = false }) => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadOverview = useCallback(async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await axios.get(`${API}/admin/operations/overview`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setOverview(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || 'Impossible de charger la vue opérations';
      toast.error(typeof message === 'string' ? message : 'Impossible de charger la vue opérations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
    const interval = setInterval(() => loadOverview(true), 30000);
    return () => clearInterval(interval);
  }, [loadOverview]);

  const actionRows = Object.entries(overview?.actions_required || {});
  const failedJobs = (overview?.automations?.recent_failed_jobs || []).slice(0, 8);
  const auditAnomalies = (overview?.recent_audit_anomalies || []).slice(0, 8);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          {!embedded && <h2 className="text-2xl font-semibold">Vue opérations</h2>}
          <p className="text-sm text-gray-500">
            Suivi pragmatique des zones sensibles du backend
          </p>
        </div>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => loadOverview(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-8 text-sm text-gray-500">Chargement de la vue opérations...</CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Utilisateurs</CardDescription>
                <CardTitle>{numberOrZero(overview?.users?.total)}</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-gray-600">
                <div>Vendeurs : {numberOrZero(overview?.users?.vendors)}</div>
                <div>Livreurs : {numberOrZero(overview?.users?.couriers)}</div>
                <div>Admins : {numberOrZero(overview?.users?.admins)}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Livraisons</CardDescription>
                <CardTitle>{numberOrZero(overview?.deliveries?.today)} aujourd’hui</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-gray-600">
                <div>Paiements en attente : {numberOrZero(overview?.deliveries?.pending_payment)}</div>
                <div>En attente d’attribution : {numberOrZero(overview?.deliveries?.awaiting_assignment)}</div>
                <div>En échec : {numberOrZero(overview?.deliveries?.failed)}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Paiements / Portefeuille</CardDescription>
                <CardTitle>{numberOrZero(overview?.payments?.pending)} en attente</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-gray-600">
                <div>Paiements en échec : {numberOrZero(overview?.payments?.failed)}</div>
                <div>Recharges en attente : {numberOrZero(overview?.wallets?.pending_recharges)}</div>
                <div>Recharges en échec : {numberOrZero(overview?.wallets?.failed_recharges)}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Conformité / Intégrations</CardDescription>
                <CardTitle>{numberOrZero(overview?.kyc?.pending)} KYC en attente</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-gray-600">
                <div>Intégrations connectées : {numberOrZero(overview?.integrations?.connected)}</div>
                <div>Intégrations déconnectées : {numberOrZero(overview?.integrations?.disconnected)}</div>
                <div>Paiements d’affiliation en attente : {numberOrZero(overview?.affiliation?.payouts_pending)}</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Actions requises</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {actionRows.length === 0 ? (
                  <div className="text-sm text-gray-500">Aucune action immédiate détectée.</div>
                ) : (
                  actionRows.map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between rounded-md border p-2">
                      <span className="text-sm text-gray-700">{formatOpsActionLabel(key)}</span>
                      <Badge variant={numberOrZero(value) > 0 ? 'destructive' : 'outline'}>
                        {numberOrZero(value)}
                      </Badge>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  Santé des automatisations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>Jobs en attente</span>
                  <Badge variant="outline">{numberOrZero(overview?.automations?.pending)}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>Jobs en échec</span>
                  <Badge variant={numberOrZero(overview?.automations?.failed) > 0 ? 'destructive' : 'outline'}>
                    {numberOrZero(overview?.automations?.failed)}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>Échecs email (24 h)</span>
                  <Badge
                    variant={numberOrZero(overview?.automations?.email_dispatch_failures_last_24h) > 0 ? 'destructive' : 'outline'}
                  >
                    {numberOrZero(overview?.automations?.email_dispatch_failures_last_24h)}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  Jobs récents en échec
                </CardTitle>
              </CardHeader>
              <CardContent>
                {failedJobs.length === 0 ? (
                  <div className="text-sm text-gray-500">Aucun job en échec dans l’échantillon récent.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Erreur</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {failedJobs.map((job) => (
                        <TableRow key={job.id}>
                          <TableCell className="text-xs">{job.type || '-'}</TableCell>
                          <TableCell><Badge variant="destructive">{job.status || 'failed'}</Badge></TableCell>
                          <TableCell className="max-w-[280px] truncate text-xs text-gray-600">
                            {job.last_error || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Anomalies d’audit récentes</CardTitle>
              </CardHeader>
              <CardContent>
                {auditAnomalies.length === 0 ? (
                  <div className="text-sm text-gray-500">Aucune anomalie d’audit récente.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Ressource</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditAnomalies.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="text-xs">{item.type || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={item.status === 'failed' ? 'destructive' : 'secondary'}>
                              {item.status || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-gray-600">
                            {item.resource_type || '-'} / {item.resource_id || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default OperationsOverview;
